import { useState, useEffect, useCallback, useRef } from 'react';
// // import { parseBlob } from 'music-metadata-browser'; // Temporarily disabled
import { Buffer } from 'buffer';
import { audioPerformanceLogger } from '@/utils/audioPerformanceLogger';
import { getAudioDiagnosticsReport, startAudioDiagnostics } from '@/utils/audioContextDiagnostics';
import { 
  initializeBufferMonitor, 
  correlateWithUltraLowAudio, 
  stopBufferMonitoring 
} from '@/utils/audioBufferMonitor';
import { bufferOptimizer } from '@/utils/bufferOptimizer';

if (typeof window !== 'undefined') {
  window.Buffer = Buffer;
}

export function useAudioPlayer(audioRef, analyserRef, dataArrayRef) {
  const [currentSong, setCurrentSongInternal] = useState(null); 
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDurationInternal] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isLooping, setIsLooping] = useState(false);
  const [isPlaylistLooping, setIsPlaylistLooping] = useState(false); 
  
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const gainNodeRef = useRef(null);
  const currentFileObjectUrlRef = useRef(null); // For the audio file itself
  const loadedMetadataHandlerRef = useRef(null); 

  const setCurrentSong = useCallback((song) => {
    // When current song changes, ensure its duration is set in the player state.
    // This is crucial if the song object from playlist already has duration.
    if (song && typeof song.duration === 'number' && song.duration > 0) {
      setDurationInternal(song.duration);
    } else if (!song) {
      setDurationInternal(0); // Reset duration if no song
    }
    setCurrentSongInternal(song);
  }, []);

  const setDuration = useCallback((newDuration) => {
    console.log(`⏱️ useAudioPlayer: setDuration called with ${newDuration}`);
    setDurationInternal(newDuration);
    // Also update the duration on the currentSong object if it's loaded
    setCurrentSongInternal(prevSong => {
      if (prevSong && prevSong.duration !== newDuration) {
        return { ...prevSong, duration: newDuration };
      }
      return prevSong;
    });
  }, []);


  const initializeAudioContext = useCallback(() => {
    if (audioContextRef.current && gainNodeRef.current && analyserRef.current) {
      // Already initialized, do not recreate nodes
      console.log('🎵 useAudioPlayer: AudioContext already initialized, skipping...');
      // Generate diagnostics report for existing context
      const report = getAudioDiagnosticsReport();
      audioPerformanceLogger.info('audio', 'AudioContext reuse detected', report);
      return true;
    }
    if (!audioContextRef.current) {
      console.log('🎵 useAudioPlayer: Initializing AudioContext...');
      try {
        // Enhanced AudioContext configuration for Electron
        const isElectron = typeof window !== 'undefined' && window.electronAPI;
        const audioContextOptions = isElectron ? {
          // Larger buffer size for Electron to reduce underruns
          latencyHint: 'playback',
          sampleRate: 44100
        } : {};
        
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)(audioContextOptions);
        
        // DIAGNOSTIC: Start comprehensive audio tracking
        console.log('🔍 [DIAGNOSTICS] Initializing audio diagnostics for new AudioContext...');
        const diagnosticReport = startAudioDiagnostics(null); // Will set audio element later
        audioPerformanceLogger.info('audio', 'AudioContext diagnostics initialized', diagnosticReport);
        
      } catch (error) {
        console.error('❌ useAudioPlayer: Failed to initialize AudioContext:', error);
        return false;
      }
    }
    if (!analyserRef.current) {
      const analyser = audioContextRef.current.createAnalyser();
      // Enhanced stability settings for Electron
      const isElectron = typeof window !== 'undefined' && window.electronAPI;
      
      // Balanced settings: stability + responsiveness optimized for performance
      analyser.fftSize = isElectron ? 512 : 512; // Good frequency resolution
      analyser.smoothingTimeConstant = isElectron ? 0.8 : 0.8; // Slightly increased for stability while maintaining responsiveness
      
      // Wider dynamic range for better frequency detection
      analyser.minDecibels = -100;
      analyser.maxDecibels = -10;
      
      analyserRef.current = analyser;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      dataArrayRef.current = dataArray;
      
      console.log(`🎯 useAudioPlayer: Enhanced analyser initialized for ${isElectron ? 'Electron' : 'Web'}`, {
        fftSize: analyser.fftSize,
        smoothing: analyser.smoothingTimeConstant,
        bufferLength,
        minDecibels: analyser.minDecibels,
        maxDecibels: analyser.maxDecibels
      });
      
      audioPerformanceLogger.info('audio', 'Enhanced analyser configuration applied', {
        platform: isElectron ? 'electron' : 'web',
        fftSize: analyser.fftSize,
        smoothing: analyser.smoothingTimeConstant,
        dynamicRange: analyser.maxDecibels - analyser.minDecibels
      });
    }
    if (!gainNodeRef.current) {
      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = volume;
      gainNodeRef.current = gainNode;
      // Connect gainNode -> analyser -> destination ONCE
      gainNode.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);
    }
    console.log('🎵 useAudioPlayer: AudioContext initialized successfully');
    
    // DIAGNOSTIC: Generate detailed initialization report
    const diagnosticReport = getAudioDiagnosticsReport();
    console.log('🔍 [DIAGNOSTICS] AudioContext initialization complete:', diagnosticReport);
    
    audioPerformanceLogger.info('audio', 'AudioContext initialized successfully', {
      sampleRate: audioContextRef.current.sampleRate,
      state: audioContextRef.current.state,
      baseLatency: audioContextRef.current.baseLatency || 'not available',
      outputLatency: audioContextRef.current.outputLatency || 'not available',
      diagnostics: diagnosticReport
    });
    return true;
  }, [analyserRef, dataArrayRef, volume]);

  const connectAudioSource = useCallback(() => {
    if (!audioRef.current || !audioContextRef.current || !analyserRef.current || !gainNodeRef.current) {
      console.warn('❌ useAudioPlayer: Cannot connect audio source - missing refs.');
      return;
    }
    
    // DIAGNOSTIC: Start monitoring audio element
    // audioContextDiagnostics.startAudioOutputMonitoring(audioRef.current); // Simplified version doesn't need this
    
    try {
      // Only create the MediaElementSourceNode if it doesn't exist or if the mediaElement has changed
      if (!sourceRef.current || sourceRef.current.mediaElement !== audioRef.current) {
        console.log('🔗 [DIAGNOSTICS] Creating new MediaElementSource connection...');
        
        if (sourceRef.current) {
          // Only disconnect if the mediaElement is different (old audio element)
          console.log('🔗 [DIAGNOSTICS] Disconnecting previous MediaElementSource...');
          try { sourceRef.current.disconnect(); } catch (e) {
            console.warn('🔗 [DIAGNOSTICS] Disconnect warning:', e.message);
          }
        }
        
        sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
        sourceRef.current.connect(gainNodeRef.current);
        // Attach gainNode to audio element for normalization logic
        audioRef.current.gainNode = gainNodeRef.current;
        
        // DIAGNOSTIC: Log connection details
        const connectionReport = getAudioDiagnosticsReport();
        console.log('🔗 [DIAGNOSTICS] MediaElementSource connected:', connectionReport);
        audioPerformanceLogger.info('audio', 'MediaElementSource connection established', connectionReport);
        
        // Electron-specific: Enhanced connection stability monitoring
        const isElectron = typeof window !== 'undefined' && window.electronAPI;
        if (isElectron) {
          // Monitor connection health periodically
          if (window.audioConnectionHealthCheck) {
            clearInterval(window.audioConnectionHealthCheck);
          }
          
          window.audioConnectionHealthCheck = setInterval(() => {
            if (analyserRef.current && dataArrayRef.current && audioRef.current && !audioRef.current.paused) {
              analyserRef.current.getByteFrequencyData(dataArrayRef.current);
              const testSample = Array.from(dataArrayRef.current.slice(0, 8));
              const hasData = testSample.some(v => v > 10);
              
              // More targeted corruption detection - only catch severe cases
              const isCorrupted = 
                // Severe: all data in first bin only with high amplitude
                (testSample[0] > 100 && testSample.slice(1).every(v => v < 5)) ||
                // Severe: completely silent analysis during loud playback
                (testSample.every(v => v < 5) && !audioRef.current.paused && audioRef.current.volume > 0.5);
              
              if (isCorrupted) {
                console.warn('🔧 useAudioPlayer: Severe audio analysis corruption detected, attempting recovery...', {
                  sample: testSample,
                  paused: audioRef.current.paused,
                  currentTime: audioRef.current.currentTime
                });
                
                // Streamlined recovery strategy
                try {
                  // Method 1: Disconnect and reconnect source
                  sourceRef.current.disconnect();
                  sourceRef.current.connect(gainNodeRef.current);
                  
                  // Method 2: Refresh data array
                  const bufferLength = analyserRef.current.frequencyBinCount;
                  dataArrayRef.current = new Uint8Array(bufferLength);
                  
                  console.log('🔧 useAudioPlayer: Audio analysis recovery completed');
                  audioPerformanceLogger.warn('audio', 'Severe audio analysis corruption recovered', {
                    corruptedSample: testSample,
                    recoveryMethods: ['reconnect', 'refresh_buffer']
                  });
                } catch (error) {
                  console.error('🔧 useAudioPlayer: Recovery failed:', error);
                  audioPerformanceLogger.error('audio', 'Audio recovery failed', {
                    error: error.message,
                    corruptedSample: testSample
                  });
                }
              }
            }
          }, 5000); // Less frequent checks (every 5 seconds) to avoid interference
        }
        
        console.log('🎵 useAudioPlayer: Audio source connected (new or changed audio element).');
      } else {
        // Already connected, do nothing
        // console.log('🎵 useAudioPlayer: Audio source already connected.');
      }
    } catch (error) {
      console.error('❌ useAudioPlayer: Failed to connect audio source:', error);
    }
  }, [audioRef, analyserRef]); 

  const resumeAudioContext = useCallback(async () => {
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      console.log('🎵 useAudioPlayer: Resuming AudioContext...');
      try {
        await audioContextRef.current.resume();
        console.log('🎵 useAudioPlayer: AudioContext resumed.');
      } catch (error) {
        console.error('❌ useAudioPlayer: Failed to resume AudioContext:', error);
      }
    }
  }, []);

  const loadSong = useCallback(async (songToLoad) => {
    if (!songToLoad || !songToLoad.file || !(songToLoad.file instanceof File)) {
      console.warn('❌ useAudioPlayer: Invalid song or file provided to loadSong.', songToLoad);
      if (currentFileObjectUrlRef.current) {
        URL.revokeObjectURL(currentFileObjectUrlRef.current);
        currentFileObjectUrlRef.current = null;
      }
      // Album art URL is part of songToLoad and managed by usePlaylist, no need to revoke here
      setCurrentSong(null);
      setIsPlaying(false);
      // setDuration(0); // Duration will be reset by setCurrentSong(null)
      setCurrentTime(0);
      if (audioRef.current) audioRef.current.src = '';
      return null; 
    }

    console.log(`🎵 useAudioPlayer: Attempting to load song: ${songToLoad.name}`);
    
    if (currentFileObjectUrlRef.current) {
      URL.revokeObjectURL(currentFileObjectUrlRef.current);
      currentFileObjectUrlRef.current = null;
      console.log('🗑️ useAudioPlayer: Revoked previous song file ObjectURL.');
    }
    // Album art URL is now expected to be on songToLoad from usePlaylist

    setIsPlaying(false); 
    setCurrentTime(0); // Reset current time for new song

    try {
      if (!initializeAudioContext()) {
        throw new Error('Failed to initialize audio context for song load.');
      }

      // songToLoad should already have metadata (title, artist, album, duration, albumArt blob URL) from usePlaylist
      let finalSongData = { ...songToLoad };

      // Create ObjectURL for the audio file
      finalSongData.url = URL.createObjectURL(finalSongData.file);
      currentFileObjectUrlRef.current = finalSongData.url;
      console.log(`🔗 useAudioPlayer: Song file ObjectURL created for ${finalSongData.name}: ${finalSongData.url}`);
      
      setCurrentSong(finalSongData); // Set current song with all available data (including pre-parsed duration and albumArt)
      
      // If songToLoad already has a duration, useAudioPlayer's duration state is updated by setCurrentSong's effect.
      // If not, set to 0 initially, to be updated by 'loadedmetadata' or 'canplay'.
      if (typeof finalSongData.duration !== 'number' || finalSongData.duration <= 0) {
        setDuration(0); 
      }


      if (audioRef.current) {
        audioRef.current.src = finalSongData.url;
        audioRef.current.volume = 1.0; // Always set to 1.0, all volume control is via gain node
        audioRef.current.loop = isLooping;
        // Enhanced preloading for Electron builds to prevent buffer underruns
        const isElectron = typeof window !== 'undefined' && window.electronAPI;
        audioRef.current.preload = isElectron ? 'auto' : 'metadata';
        
        // Critical: Add real-time buffer monitoring for Electron
        if (isElectron) {
          // Monitor buffer health in real-time during playback
          const bufferHealthCheck = () => {
            if (!audioRef.current || audioRef.current.paused) return;
            
            const buffered = audioRef.current.buffered;
            const currentTime = audioRef.current.currentTime;
            const duration = audioRef.current.duration;
            
            if (buffered.length > 0) {
              const bufferEnd = buffered.end(buffered.length - 1);
              const bufferAhead = bufferEnd - currentTime;
              
              // Critical threshold: less than 2 seconds buffered ahead
              if (bufferAhead < 2 && currentTime < duration - 3) {
                console.warn('🚨 useAudioPlayer: Low buffer detected!', {
                  bufferAhead: bufferAhead.toFixed(2),
                  currentTime: currentTime.toFixed(2),
                  bufferEnd: bufferEnd.toFixed(2),
                  readyState: audioRef.current.readyState
                });
                
                audioPerformanceLogger.warn('audio', 'Low buffer warning - preemptive action', {
                  bufferAhead,
                  currentTime,
                  readyState: audioRef.current.readyState,
                  networkState: audioRef.current.networkState
                });
                
                // Preemptive action: force buffer refill
                if (audioRef.current.readyState < 3) {
                  console.log('🔧 useAudioPlayer: Forcing buffer refill...');
                  const currentPos = audioRef.current.currentTime;
                  audioRef.current.load();
                  audioRef.current.currentTime = currentPos;
                  audioRef.current.play().catch(e => console.warn('Buffer refill play failed:', e));
                }
              }
            }
          };
          
          // Check buffer health every 500ms during playback
          audioRef.current.bufferHealthInterval = setInterval(bufferHealthCheck, 500);
        }
        
        if (loadedMetadataHandlerRef.current) {
            audioRef.current.removeEventListener('loadedmetadata', loadedMetadataHandlerRef.current);
        }

        loadedMetadataHandlerRef.current = () => {
          console.log(`✅ useAudioPlayer: <audio> 'loadedmetadata' for ${finalSongData.name}. Element duration: ${audioRef.current.duration}`);
          
          // CRITICAL FIX: Connect audio source immediately when metadata is loaded
          // This ensures the audio path is established before any play attempts
          connectAudioSource();
          console.log(`🔗 useAudioPlayer: Audio source connected during loadedmetadata for ${finalSongData.name}`);
          
          // BUFFER UNDERRUN INVESTIGATION: Initialize buffer monitoring
          console.log(`🔍 [BUFFER] Initializing buffer monitoring for ${finalSongData.name}`);
          initializeBufferMonitor(audioRef.current);
          audioPerformanceLogger.info('buffer', 'Buffer monitoring initialized for new track', {
            track: finalSongData.name,
            readyState: audioRef.current.readyState,
            networkState: audioRef.current.networkState
          });
          
          // BUFFER OPTIMIZATION: Initialize buffer optimization system
          console.log(`🚀 [BUFFER] Initializing buffer optimization for ${finalSongData.name}`);
          bufferOptimizer.initializeBufferOptimization(audioRef.current, [finalSongData]);
          audioPerformanceLogger.info('buffer', 'Buffer optimization initialized for new track', {
            track: finalSongData.name,
            readyState: audioRef.current.readyState,
            networkState: audioRef.current.networkState
          });
          
          // Enhanced logging for dropout debugging
          audioPerformanceLogger.info('audio', 'Audio connection established during loadedmetadata', {
            track: finalSongData.name,
            readyState: audioRef.current.readyState,
            networkState: audioRef.current.networkState,
            hasSource: !!sourceRef.current,
            audioContextState: audioContextRef.current?.state
          });
          
          const audioElementDuration = audioRef.current.duration;
          if (audioElementDuration && !isNaN(audioElementDuration) && isFinite(audioElementDuration) && audioElementDuration > 0) {
            console.log(`⏱️ useAudioPlayer: Updating duration from <audio> element to ${audioElementDuration} for ${finalSongData.name}`);
            setDuration(audioElementDuration); // This will update currentSong's duration as well via its effect
          } else if (finalSongData.duration && finalSongData.duration > 0) {
            console.log(`⏱️ useAudioPlayer: Using pre-parsed duration ${finalSongData.duration} for ${finalSongData.name} as <audio> element duration is invalid.`);
            setDuration(finalSongData.duration); // Fallback to pre-parsed if audio element is weird
          } else {
            console.warn(`⚠️ useAudioPlayer: No valid duration found for ${finalSongData.name} from <audio> element or pre-parsing.`);
          }
        };
        audioRef.current.addEventListener('loadedmetadata', loadedMetadataHandlerRef.current, { once: true });
        
        // Add a backup connection strategy for canplay event 
        // in case loadedmetadata doesn't fire or connection fails
        const canPlayHandler = () => {
          console.log(`🎯 useAudioPlayer: 'canplay' event for ${finalSongData.name}`);
          // Ensure connection is established when audio is ready to play
          if (!sourceRef.current || sourceRef.current.mediaElement !== audioRef.current) {
            console.log(`🔧 useAudioPlayer: Establishing backup audio connection during canplay`);
            connectAudioSource();
            audioPerformanceLogger.info('audio', 'Backup audio connection established during canplay', {
              track: finalSongData.name,
              readyState: audioRef.current.readyState,
              reason: 'loadedmetadata connection missing'
            });
          } else {
            console.log(`✅ useAudioPlayer: Audio connection already established for ${finalSongData.name}`);
          }
        };
        audioRef.current.addEventListener('canplay', canPlayHandler, { once: true });
        
        audioRef.current.load(); // Important to trigger loading and 'loadedmetadata'
      }
      
      console.log(`👍 useAudioPlayer: Song prepared: ${finalSongData.title}. Initial duration from song object: ${finalSongData.duration}`);
      return finalSongData; 
    } catch (error) {
      console.error(`❌ useAudioPlayer: Error loading song ${songToLoad.name}:`, error);
      if (currentFileObjectUrlRef.current) {
        URL.revokeObjectURL(currentFileObjectUrlRef.current);
        currentFileObjectUrlRef.current = null;
      }
      setCurrentSong(null);
      // setDuration(0);
      return null;
    }
  }, [audioRef, volume, isLooping, initializeAudioContext, connectAudioSource, setCurrentSong, setDuration]); 

  const play = useCallback(async () => {
    if (!audioRef.current || !currentSong || !currentSong.url) {
      console.warn('❌ useAudioPlayer: Cannot play - audio element, currentSong, or URL missing.');
      return false;
    }
    try {
      await resumeAudioContext();
      
      // Verify audio connection exists, reconnect only if necessary
      if (!sourceRef.current || sourceRef.current.mediaElement !== audioRef.current) {
        console.log('🔧 useAudioPlayer: Audio source needs reconnection during play');
        connectAudioSource();
      }
      
      await audioRef.current.play();
      setIsPlaying(true);
      console.log(`▶️ useAudioPlayer: Playing ${currentSong.title}`);
      audioPerformanceLogger.info('audio', `Started playback: ${currentSong.title}`, {
        duration: currentSong.duration,
        currentTime: audioRef.current.currentTime,
        readyState: audioRef.current.readyState
      });
      return true;
    } catch (error) {
      console.error(`❌ useAudioPlayer: Error playing ${currentSong.title}:`, error);
      audioPerformanceLogger.error('audio', `Playback failed: ${currentSong.title}`, {
        error: error.message,
        name: error.name,
        readyState: audioRef.current?.readyState,
        networkState: audioRef.current?.networkState
      });
      setIsPlaying(false);
      return false;
    }
  }, [audioRef, currentSong, resumeAudioContext, connectAudioSource]);

  const pause = useCallback(() => {
    if (!audioRef.current) return;
    try {
      audioRef.current.pause();
      setIsPlaying(false);
      if (currentSong) console.log(`⏸️ useAudioPlayer: Paused ${currentSong.title}`);
    } catch (error) {
      console.error('❌ useAudioPlayer: Error pausing:', error);
    }
  }, [audioRef, currentSong]);

  const seek = useCallback((time) => {
    if (!audioRef.current || isNaN(time) || !currentSong) return; // Ensure currentSong exists for duration
    const newTime = Math.max(0, Math.min(time, duration || 0));
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime); 
  }, [audioRef, duration, currentSong]);

  const handleVolumeChange = useCallback(async (newVolume) => {
    // Always ensure audio context is valid before changing gain
    initializeAudioContext();
    // Resume context if suspended
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        console.log('🎵 useAudioPlayer: AudioContext resumed in handleVolumeChange.');
      } catch (e) {
        console.warn('⚠️ useAudioPlayer: Failed to resume AudioContext in handleVolumeChange:', e);
      }
    }
    // Reconnect source if missing
    if (!sourceRef.current) {
      connectAudioSource();
      console.log('🎵 useAudioPlayer: connectAudioSource called in handleVolumeChange.');
    }
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    if (gainNodeRef.current) gainNodeRef.current.gain.value = clampedVolume;
    // Deep debug logging for volume change freeze bug
    console.log('🔊 useAudioPlayer: handleVolumeChange called:', {
      newVolume,
      clampedVolume,
      gainNode: gainNodeRef.current,
      gainValue: gainNodeRef.current ? gainNodeRef.current.gain.value : undefined,
      audioContext: audioContextRef.current,
      audioContextState: audioContextRef.current ? audioContextRef.current.state : undefined,
      analyserNode: analyserRef.current,
      analyserNodeIsConnected: (() => {
        try {
          if (audioContextRef.current && analyserRef.current) {
            return analyserRef.current.numberOfOutputs > 0;
          }
        } catch (e) {}
        return undefined;
      })(),
      sourceNode: sourceRef.current,
      sourceNodeIsConnected: (() => {
        try {
          if (sourceRef.current && gainNodeRef.current) {
            return sourceRef.current.numberOfOutputs > 0;
          }
        } catch (e) {}
        return undefined;
      })(),
      dataArrayRefType: dataArrayRef.current ? dataArrayRef.current.constructor.name : undefined,
      dataArrayRefLength: dataArrayRef.current ? dataArrayRef.current.length : undefined,
    });
  }, [initializeAudioContext, connectAudioSource]); 

  const toggleLoop = useCallback(() => {
    setIsLooping(prev => {
      const newValue = !prev;
      if (audioRef.current) audioRef.current.loop = newValue;
      console.log(`🔄 useAudioPlayer: Song loop toggled to ${newValue}`);
      return newValue;
    });
  }, [audioRef]);
  
  const setContextPlaylistLooping = useCallback((value) => {
    setIsPlaylistLooping(value);
  }, []);


  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentSong) return; // Only attach if there's an audio element and a song

    // Throttle time updates to improve performance (30fps instead of default ~250fps)
    let lastTimeUpdate = 0;
    const handleTimeUpdate = () => {
      const now = Date.now();
      if (now - lastTimeUpdate >= 33) { // ~30fps (33ms)
        setCurrentTime(audio.currentTime);
        lastTimeUpdate = now;
      }
    };
    
    const handleCanPlay = () => {
        const audioElementDuration = audio.duration;
        if (audioElementDuration && !isNaN(audioElementDuration) && isFinite(audioElementDuration) && audioElementDuration > 0) {
            if (duration !== audioElementDuration) { // Compare with player's duration state
                 console.log(`🔄 useAudioPlayer: Duration updated via 'canplay' for ${currentSong?.name}: ${audioElementDuration}`);
                 setDuration(audioElementDuration); // This will update currentSong's duration via its effect
            }
        }
    };

    const handleEnded = () => {
      console.log(`🔚 useAudioPlayer: Song ended: ${currentSong?.name}`);
      if (!audio.loop) { 
        setIsPlaying(false);
      } else { // Native loop
        setCurrentTime(0);
        if (audioContextRef.current?.state === 'running') { // Only try to play if context is running
            audio.play().catch(e => console.error("Error replaying natively looped song:", e));
        }
      }
    };
    const handlePlayEvent = () => setIsPlaying(true);
    const handlePauseEvent = () => setIsPlaying(false); 
    const handleErrorEvent = (e) => {
      console.error(`❌ useAudioPlayer: Audio Element Error for ${currentSong?.name}:`, e);
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('canplay', handleCanPlay); 
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlayEvent);
    audio.addEventListener('pause', handlePauseEvent);
    audio.addEventListener('error', handleErrorEvent);

    // Enhanced audio health monitoring for Electron
    let audioHealthCheckInterval = null;
    let handleWaiting, handleSuspend, handleStalled;
    const isElectron = typeof window !== 'undefined' && window.electronAPI;
    
    if (isElectron) {
      console.log('🔧 useAudioPlayer: Starting Electron audio health monitoring');
      
      // Add buffer monitoring event listeners specifically for Electron
      handleWaiting = () => {
        console.warn('⚠️ useAudioPlayer: Audio element waiting for data');
        audioPerformanceLogger.warn('audio', 'Audio element waiting for data');
      };
      
      handleSuspend = () => {
        console.warn('⚠️ useAudioPlayer: Audio element suspended');
        audioPerformanceLogger.warn('audio', 'Audio element suspended');
      };
      
      handleStalled = () => {
        console.warn('⚠️ useAudioPlayer: Audio element stalled');
        audioPerformanceLogger.warn('audio', 'Audio element stalled');
      };
      
      audio.addEventListener('waiting', handleWaiting);
      audio.addEventListener('suspend', handleSuspend);
      audio.addEventListener('stalled', handleStalled);
      
      // Primary health check - comprehensive monitoring
      audioHealthCheckInterval = setInterval(() => {
        // Check for buffer underruns and audio thread health
        if (audio.readyState < 2 && !audio.paused && !audio.ended) {
          console.warn('⚠️ useAudioPlayer: Audio buffer underrun detected, attempting recovery');
          audioPerformanceLogger.warn('audio', 'Buffer underrun detected', {
            readyState: audio.readyState,
            paused: audio.paused,
            ended: audio.ended,
            currentTime: audio.currentTime,
            duration: audio.duration,
            networkState: audio.networkState
          });
          
          // Enhanced recovery strategy: attempt to reload if multiple underruns
          const currentTimeBeforeRecover = audio.currentTime;
          
          // First try: brief pause-resume cycle
          audio.pause();
          
          setTimeout(() => {
            if (audio.src && !audio.ended) {
              // For persistent underruns, try reloading the audio element
              if (audio.readyState < 2) {
                console.log('🔄 useAudioPlayer: Attempting audio element reload for persistent underrun');
                audio.load();
                setTimeout(() => {
                  audio.currentTime = currentTimeBeforeRecover;
                  audio.play().catch(err => {
                    console.error('❌ useAudioPlayer: Failed to recover from underrun after reload:', err);
                    audioPerformanceLogger.error('audio', 'Buffer underrun recovery failed after reload', {
                      error: err.message,
                      currentTime: currentTimeBeforeRecover
                    });
                  });
                }, 100);
              } else {
                audio.currentTime = currentTimeBeforeRecover;
                audio.play().catch(err => {
                  console.error('❌ useAudioPlayer: Failed to recover from buffer underrun:', err);
                  audioPerformanceLogger.error('audio', 'Buffer underrun recovery failed', {
                    error: err.message,
                    currentTime: currentTimeBeforeRecover
                  });
                });
              }
            }
          }, 50);
        }
        
        // Check AudioContext health
        if (audioContextRef.current && audioContextRef.current.state !== 'running' && !audio.paused) {
          console.warn('⚠️ useAudioPlayer: AudioContext not running during playback, attempting resume');
          audioPerformanceLogger.warn('audio', 'AudioContext suspended during playback', {
            contextState: audioContextRef.current.state,
            audioCurrentTime: audio.currentTime,
            audioPaused: audio.paused
          });
          
          audioContextRef.current.resume().catch(err => {
            console.error('❌ useAudioPlayer: Failed to resume AudioContext:', err);
            audioPerformanceLogger.error('audio', 'Failed to resume AudioContext', {
              error: err.message,
              contextState: audioContextRef.current.state
            });
          });
        }
        
        // Force garbage collection hint in Electron (if available) - more aggressive for crash prevention
        if (window.gc && Math.random() < 0.3) { // Increased to 30% chance every 5 seconds for stability
          try {
            window.gc();
            console.log('[MEMORY] Manual garbage collection triggered');
          } catch (err) {
            // Silent fail - gc might not be available
          }
        }
        
        // Additional memory pressure relief for Electron
        if (typeof window !== 'undefined' && window.electronAPI) {
          // Clear any accumulated audio buffer references
          if (typeof window.performance !== 'undefined' && window.performance.memory) {
            const memInfo = window.performance.memory;
            if (memInfo.usedJSHeapSize > memInfo.totalJSHeapSize * 0.8) {
              console.warn('[MEMORY] High memory usage detected, triggering cleanup');
              // Force a more aggressive cleanup cycle
              setTimeout(() => {
                if (window.gc) {
                  try {
                    window.gc();
                  } catch (err) {
                    console.error('GC failed:', err);
                  }
                }
              }, 100);
            }
          }
        }
        
      }, 5000);
    }

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlayEvent);
      audio.removeEventListener('pause', handlePauseEvent);
      audio.removeEventListener('error', handleErrorEvent);
      
      // Clean up Electron-specific event listeners
      if (isElectron) {
        audio.removeEventListener('waiting', handleWaiting);
        audio.removeEventListener('suspend', handleSuspend);
        audio.removeEventListener('stalled', handleStalled);
      }
      
      // Clean up Electron health monitoring
      if (audioHealthCheckInterval) {
        clearInterval(audioHealthCheckInterval);
        audioHealthCheckInterval = null;
      }
      
      if (loadedMetadataHandlerRef.current) { 
        audio.removeEventListener('loadedmetadata', loadedMetadataHandlerRef.current);
        loadedMetadataHandlerRef.current = null;
      }
      
      // Memory cleanup for crash prevention
      if (typeof window !== 'undefined' && window.electronAPI && window.gc) {
        try {
          window.gc(); // Force cleanup on audio element cleanup
        } catch (err) {
          // Silent fail
        }
      }
    };
  }, [audioRef, currentSong, duration, setDuration]); // Added duration and setDuration for canplay check

  // Only run this effect on mount/unmount
  useEffect(() => {
    const setupAudioContextOnInteraction = () => {
      initializeAudioContext();
      document.removeEventListener('click', setupAudioContextOnInteraction, true);
      document.removeEventListener('keydown', setupAudioContextOnInteraction, true);
    };
    document.addEventListener('click', setupAudioContextOnInteraction, true);
    document.addEventListener('keydown', setupAudioContextOnInteraction, true);

    return () => {
      document.removeEventListener('click', setupAudioContextOnInteraction, true);
      document.removeEventListener('keydown', setupAudioContextOnInteraction, true);
      if (currentFileObjectUrlRef.current) {
        URL.revokeObjectURL(currentFileObjectUrlRef.current);
        console.log('🗑️ useAudioPlayer: Revoked song file ObjectURL on unmount.');
      }
      if (sourceRef.current) {
        try { sourceRef.current.disconnect(); } catch(e){}
        sourceRef.current = null;
      }
      
      // Clean up connection health monitoring
      if (window.audioConnectionHealthCheck) {
        clearInterval(window.audioConnectionHealthCheck);
        window.audioConnectionHealthCheck = null;
      }
      
      // Clean up buffer health monitoring
      if (audioRef.current?.bufferHealthInterval) {
        clearInterval(audioRef.current.bufferHealthInterval);
        audioRef.current.bufferHealthInterval = null;
      }
      
      // Clean up buffer monitoring system
      stopBufferMonitoring();
      
      // Clean up buffer optimization system
      bufferOptimizer.stopBufferOptimization();
      
      // Only close audio context on final unmount
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          console.warn('🛑 useAudioPlayer: Closing AudioContext (teardown/unmount). Call stack:', new Error().stack);
          audioContextRef.current.close();
        } catch(e){}
      }
    };
  }, []); // <-- Only run on mount/unmount

  return {
    currentSong, 
    isPlaying,
    currentTime,
    duration, // This is the duration state from useAudioPlayer
    volume,
    isLooping, 
    isPlaylistLooping, 
    play,
    pause,
    seek,
    setVolume: handleVolumeChange,
    toggleLoop, 
    setIsPlaylistLooping: setContextPlaylistLooping, 
    loadSong
  };
}